<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useDebounceFn } from '@vueuse/core';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CampaignsAPI from 'dashboard/api/campaigns';
import CampaignAnalyticsSummary from './CampaignAnalyticsSummary.vue';
import CampaignAnalyticsFilters from './CampaignAnalyticsFilters.vue';
import CampaignAnalyticsTable from './CampaignAnalyticsTable.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();

const campaignId = computed(() => route.params.campaignId);

const campaign = computed(() =>
  getters['campaigns/getAllCampaigns'].value?.find(
    item => String(item.id) === String(campaignId.value)
  )
);
const campaignTitle = computed(
  () => campaign.value?.title || t('CAMPAIGN.ANALYTICS.HEADER_TITLE')
);

const rows = ref([]);
const summary = ref({});
const hasError = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const isFetching = ref(false);

const filters = ref({ status: '', since: '', until: '', search: '' });

const fetchSentMessages = async () => {
  isFetching.value = true;
  try {
    const { data } = await CampaignsAPI.getSentMessages(campaignId.value, {
      page: currentPage.value,
      status: filters.value.status || undefined,
      since: filters.value.since || undefined,
      until: filters.value.until || undefined,
      search: filters.value.search || undefined,
    });
    rows.value = data.payload;
    summary.value = data.meta.summary;
    hasError.value = data.meta.has_error;
    currentPage.value = data.meta.current_page;
    totalPages.value = data.meta.total_pages;
  } finally {
    isFetching.value = false;
  }
};

const debouncedFetch = useDebounceFn(fetchSentMessages, 300);

const handleFilterChange = updatedFilter => {
  filters.value = { ...filters.value, ...updatedFilter };
  currentPage.value = 1;
  debouncedFetch();
};

const handleSelectStatus = status => {
  const nextStatus = filters.value.status === status ? '' : status;
  filters.value = { ...filters.value, status: nextStatus };
  currentPage.value = 1;
  fetchSentMessages();
};

const handlePageChange = page => {
  currentPage.value = page;
  fetchSentMessages();
};

const goBack = () => {
  router.push({
    name: 'campaigns_whatsapp_index',
    params: { accountId: route.params.accountId },
  });
};

onMounted(() => {
  store.dispatch('campaigns/get');
  fetchSentMessages();
});
</script>

<template>
  <div class="flex flex-col gap-4 py-6">
    <header class="flex items-center gap-2">
      <Button
        variant="ghost"
        color="slate"
        size="sm"
        icon="i-lucide-arrow-left"
        @click="goBack"
      />
      <h1 class="text-xl font-medium capitalize text-n-slate-12">
        {{ campaignTitle }}
      </h1>
    </header>

    <CampaignAnalyticsSummary
      :summary="summary"
      :active-status="filters.status"
      @select-status="handleSelectStatus"
    />

    <CampaignAnalyticsFilters
      :status="filters.status"
      :since="filters.since"
      :until="filters.until"
      :search="filters.search"
      @filter-change="handleFilterChange"
    />

    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <template v-else>
      <CampaignAnalyticsTable :rows="rows" :has-error="hasError" />
      <div
        v-if="totalPages > 1"
        class="flex items-center justify-end gap-3 text-sm text-n-slate-11"
      >
        <Button
          variant="faded"
          color="slate"
          size="sm"
          :label="t('CAMPAIGN.ANALYTICS.PAGINATION.PREVIOUS')"
          :disabled="currentPage <= 1"
          @click="handlePageChange(currentPage - 1)"
        />
        <span>
          {{
            t('CAMPAIGN.ANALYTICS.PAGINATION.PAGE_INFO', {
              current: currentPage,
              total: totalPages,
            })
          }}
        </span>
        <Button
          variant="faded"
          color="slate"
          size="sm"
          :label="t('CAMPAIGN.ANALYTICS.PAGINATION.NEXT')"
          :disabled="currentPage >= totalPages"
          @click="handlePageChange(currentPage + 1)"
        />
      </div>
    </template>
  </div>
</template>
